import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, ArrowRight, CreditCard, Home, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BACKEND_URL}/api/payments/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setPaymentInfo(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Pagamento completato con successo!');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < maxAttempts) {
          // Keep polling
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-2xl">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Verifica pagamento in corso...
              </h1>
              <p className="text-slate-600">
                Attendere prego, stiamo verificando il tuo pagamento.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <CheckCircle className="w-12 h-12 text-emerald-600" />
              </div>
              <h1 className="text-3xl font-bold text-emerald-700 mb-2">
                Pagamento Completato!
              </h1>
              <p className="text-slate-600 mb-6">
                Grazie per il tuo acquisto. La prenotazione è stata confermata.
              </p>

              {paymentInfo && (
                <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Riepilogo Transazione
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Importo Totale</span>
                      <span className="font-bold text-slate-900">
                        €{paymentInfo.amount?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Valuta</span>
                      <span className="font-medium text-slate-900 uppercase">
                        {paymentInfo.currency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">ID Transazione</span>
                      <span className="font-mono text-sm text-slate-700">
                        {paymentInfo.transaction_id?.slice(0, 8)}...
                      </span>
                    </div>
                    <div className="border-t pt-3 mt-3">
                      <div className="flex justify-between text-emerald-700">
                        <span className="font-semibold">Stato</span>
                        <span className="font-bold">✓ Pagato</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate('/traveler/dashboard')}
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Vai alle tue prenotazioni
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full h-12"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Torna alla Home
                </Button>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CreditCard className="w-10 h-10 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Pagamento in elaborazione
              </h1>
              <p className="text-slate-600 mb-6">
                Il pagamento è in fase di elaborazione. Riceverai una conferma via email.
              </p>
              <Button onClick={() => navigate('/traveler/dashboard')} className="w-full">
                Vai alla Dashboard
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CreditCard className="w-10 h-10 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Sessione Scaduta
              </h1>
              <p className="text-slate-600 mb-6">
                La sessione di pagamento è scaduta. Per favore riprova.
              </p>
              <Button onClick={() => navigate('/')} className="w-full">
                Torna alla Home
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CreditCard className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Errore di Verifica
              </h1>
              <p className="text-slate-600 mb-6">
                Non siamo riusciti a verificare il pagamento. Controlla la tua email o contattaci.
              </p>
              <Button onClick={() => navigate('/')} className="w-full">
                Torna alla Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
